import { getDataFromApi } from './data-service';

/**
 * Request user location from browser and Stage.
 *
 * Note! Street address and country are resolved using Google Maps server api.
 *
 * @returns {Promise<GeocodeResult>} User location.
 */
export function requestLocation() {
  return new Promise((resolve, reject) => {
    window.navigator.geolocation.getCurrentPosition((result) => {
      const coordinates = {
        lat: result.coords.latitude,
        lng: result.coords.longitude
      };
      reverseGeocode(coordinates).then(resolve).catch(reject);
    }, reject, {
      maximumAge: 10 * 60 * 1000,
      timeout: 30 * 1000,
      enableHighAccuracy: false
    });
  });
}

/**
 * Get location from coordinates.
 *
 * @param {google.maps.LatLngLiteral} coordinates Coordinates to geocode.
 * @returns {GeocodeResult} Resolved location.
 */
export async function reverseGeocode(coordinates) {
  const response = await getDataFromApi('dealers/geocode/', new URLSearchParams({ lat: coordinates.lat, lng: coordinates.lng }));
  const location = response.body?.result?.location;
  if (location) {
    return {
      ...location,
      countryCode: location.countryCode.toLowerCase(),
      type: 'user_location',
    };
  }

  throw new Error('LOCATION_NOT_FOUND');
}

/**
 * Get location from address.
 *
 * @param {string} address Address to geocode.
 * @returns {GeocodeResult} Resolved location.
 */
export async function geocode(address) {
  const response = await getDataFromApi('dealers/geocode/', new URLSearchParams({ address }));
  const location = response.body?.result?.location;
  if (location) {
    return {
      ...location,
      countryCode: location.countryCode.toLowerCase(),
      type: 'user_search',
    };
  }

  throw new Error('LOCATION_NOT_FOUND');
}

/**
 * Geocode address or location with google maps api.
 *
 * @param {google.maps.GeocoderRequest} options Google Geocoder options.
 * @returns {Promise<GeocodeResult>} Resolved address and coordinate.
 * @deprecated Use {@link reverseGeocode} or {@link geocode} instead.
 */
export function geocodeWithGoogleMaps(options) {
  return new Promise((resolve, reject) => {
    if (!window.google?.maps?.Geocoder) {
      return reject('GOOGLE_MAPS_API_UNAVAILABLE');
    }

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(options, (res, status) => {
      if (status === 'OK') {
        if (res.length > 0) {
          resolve(formatGoogleGeocoderResult(res[0]));
        }
        else {
          reject('GOOGLE_MAPS_NO_RESULTS');
        }
      }
      else {
        reject(status);
      }
    });
  });
}

/**
 * Format browser geolocation result to internal format.
 *
 * @param {GeolocationCoordinates} result Browser coordinates.
 * @return {GeocodeResult} Internal geocode result format.
 * @deprecated Use {@link requestLocation} instead.
 */
export function formatGeolocationResult(result) {
  // Read ip country from session storage value (set by location-prompts module)
  const locationFromIp = () => {
    const value = window.sessionStorage.getItem('user_location');
    try {
      return value ? JSON.parse(value)?.countryCode : false;
    }
    catch(e) {
      return false;
    }
  };

  return {
    type: 'user_location',
    countryCode: locationFromIp() || '',
    address: '',
    lat: result.latitude,
    lng: result.longitude,
  };
}

/**
 * Format google geocoder or place api result to internal format.
 *
 * @param {google.maps.GeocoderResult | google.maps.places.PlaceResult} result Google GeocoderResult or PlaceResult.
 * @return {GeocodeResult} Internal geocode result format.
 * @deprecated Use {@link reverseGeocode} or {@link geocode} instead.
 */
export function formatGoogleGeocoderResult(result) {
  const value = {
    type: 'user_search',
    countryCode: '',
    address: result.formatted_address || '',
    lat: result.geometry?.location?.lat() || null,
    lng: result.geometry?.location?.lng() || null,
  };

  if (result.address_components) {
    result.address_components.forEach(component => {
      if (component && component.types.includes('country')) {
        value.countryCode = component.short_name.toLowerCase();
      }
    });
  }

  return value;
}

/**
 * @typedef {object} GeocodeResult
 * @prop {string} type Result type: user_location, user_search, single_dealer, address, city
 * @prop {string} address Location address
 * @prop {string} countryCode Location country code
 * @prop {number} lat Location latitude
 * @prop {number} lng Location longitude
 */
